import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Plus, Search, FolderOpen, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuthStore } from '@/store/authStore'
import { projectService } from '@/services/projectService'
import ProjectCard from '@/components/ProjectCentral/ProjectCard'
import ProjectForm from '@/components/ProjectCentral/ProjectForm'

const STATUS_FILTERS = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
]

const Projects = () => {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const isAdmin = user?.role === 'admin'

  const [statusFilter, setStatusFilter] = useState('active')
  const [search, setSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editProject, setEditProject] = useState(null)
  const [archiveTarget, setArchiveTarget] = useState(null)

  const { data, isLoading, isError, refetch } = useQuery(
    ['projects', { status: statusFilter, search }],
    () => projectService.getProjects({ status: statusFilter, search: search || undefined }),
    { keepPreviousData: true }
  )

  const projects = data?.data?.projects || []

  const handleEdit = (project) => {
    setEditProject(project)
    setFormOpen(true)
  }

  const handleFormClose = (open) => {
    setFormOpen(open)
    if (!open) setEditProject(null)
  }

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return
    try {
      await projectService.archiveProject(archiveTarget.id)
      toast.success('Project archived')
      queryClient.invalidateQueries('projects')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to archive project')
    } finally {
      setArchiveTarget(null)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Projects</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {projects.length} {statusFilter !== 'all' ? statusFilter : ''} project{projects.length !== 1 ? 's' : ''}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => { setEditProject(null); setFormOpen(true) }}>
            <Plus className="h-4 w-4 mr-1.5" />
            New Project
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center border rounded-md overflow-hidden">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1.5 text-sm transition-colors ${
                statusFilter === f.value
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {isError ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <AlertCircle className="h-10 w-10 text-destructive mb-3" />
          <h3 className="font-medium text-lg">Failed to load projects</h3>
          <p className="text-muted-foreground text-sm mt-1">Something went wrong. Please try again.</p>
          <Button variant="outline" className="mt-4" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-lg" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <FolderOpen className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium text-lg">No projects found</h3>
          <p className="text-muted-foreground text-sm mt-1 max-w-xs">
            {search ? 'Try a different search term.' : isAdmin ? 'Create your first project to get started.' : 'No projects have been created yet.'}
          </p>
          {isAdmin && !search && (
            <Button className="mt-4" onClick={() => { setEditProject(null); setFormOpen(true) }}>
              <Plus className="h-4 w-4 mr-1.5" />
              New Project
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {projects.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              onEdit={handleEdit}
              onArchive={setArchiveTarget}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}

      {/* Create/Edit form */}
      <ProjectForm
        open={formOpen}
        onOpenChange={handleFormClose}
        project={editProject}
      />

      {/* Archive confirmation */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive "{archiveTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              The project will be archived and hidden from the active list. You can view it by switching to the Archived filter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveConfirm}>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default Projects
